import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

import { UsuarioInterface } from './usuarios';

export declare interface ComentarioInterface {
  id: string;
  atendimentoId: string;
  texto: string;
  foto: string;
  apelido: string;
  criadoEm: string;
  usuario: UsuarioInterface;
}

type ComentariosState = {
  comentarios: ComentarioInterface[];
  comentariosCursor: CursorInterface;
  loadingComentarios: boolean;

  loadingAddComentario: boolean;
  loadingRemoverComentario: boolean;
};

// async thunks

export const fetchComentarios = createAsyncThunk(
  'comentarios/fetchComentarios',
  async (params: { atendimentoId: string; cursor?: CursorInterface }) => {
    const { atendimentoId, cursor } = params;
    const response = await chatAPI.get(
      `/comentarios/atendimento/${atendimentoId}`,
      { params: cursor },
    );
    return {
      data: response.data.data as ComentarioInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const addComentario = createAsyncThunk(
  'comentarios/addComentario',
  async (
    params: { atendimentoId: string; texto: string },
    { rejectWithValue },
  ) => {
    try {
      const { atendimentoId, texto } = params;
      const response = await chatAPI.post(
        `/comentarios/atendimento/${atendimentoId}`,
        { texto },
      );

      return response.data as ComentarioInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removerComentario = createAsyncThunk(
  'comentarios/removerComentario',
  async (
    params: { atendimentoId: string; comentarioId: string },
    { rejectWithValue },
  ) => {
    try {
      const { atendimentoId, comentarioId } = params;
      const response = await chatAPI.delete(
        `/comentarios/atendimento/${atendimentoId}/comentario/${comentarioId}`,
      );

      return response.data as ComentarioInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// reducer

const initialState = {
  comentarios: [],
  comentariosCursor: { afterCursor: '', beforeCursor: '' },
  loadingComentarios: false,

  loadingAddComentario: false,
  loadingRemoverComentario: false,
} as ComentariosState;

export const comentariosSlice = createSlice({
  name: 'comentarios',
  initialState,
  reducers: {
    resetComentarios(state) {
      state.comentarios = initialState.comentarios;
      state.comentariosCursor = initialState.comentariosCursor;
    },
  },
  extraReducers(builder) {
    // Fetch comentarios
    builder.addCase(fetchComentarios.pending, state => {
      state.loadingComentarios = true;
    });
    builder.addCase(fetchComentarios.fulfilled, (state, action) => {
      state.comentarios = state.comentarios.concat(action.payload.data);
      state.comentariosCursor = action.payload.cursor;
      state.loadingComentarios = false;
    });
    builder.addCase(fetchComentarios.rejected, state => {
      state.loadingComentarios = false;
    });
    // Adicionar comentario
    builder.addCase(addComentario.pending, state => {
      state.loadingAddComentario = true;
    });
    builder.addCase(addComentario.fulfilled, state => {
      state.loadingAddComentario = false;
    });
    builder.addCase(addComentario.rejected, state => {
      state.loadingAddComentario = false;
    });
    // Remover comentario
    builder.addCase(removerComentario.pending, state => {
      state.loadingRemoverComentario = true;
    });
    builder.addCase(removerComentario.fulfilled, state => {
      state.loadingRemoverComentario = false;
    });
    builder.addCase(removerComentario.rejected, state => {
      state.loadingRemoverComentario = false;
    });
  },
});

// actions

export const { resetComentarios } = comentariosSlice.actions;

// selectors

export const comentariosSelector = (state: RootState) => state.comentarios;

export default comentariosSlice.reducer;
